import { Component, Input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-string-field',
  templateUrl: './string-field.component.html',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
})
export class StringFieldComponent {
  @Input() formGroup: FormGroup;
  @Input() controlName: string;
  @Input() submitted: boolean;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() maxLength: number | undefined;
  @Input() type = 'text'; // Default type is 'text'
  @Input() required = false;
  // Jeton HTML `autocomplete`. Par defaut `off` : le champ sert surtout a de la
  // donnee custom, pour laquelle l'autoremplissage n'a pas de sens. Les champs
  // qui attendent une donnee connue (mot de passe...) le precisent eux-memes
  // (RGAA 11.13). `new-password` etait code en dur ici — faux pour tout champ
  // de texte, et il y declenchait l'UI de gestionnaire de mots de passe.
  @Input() autocomplete = 'off';

  /**
   * L'identifiant du contrôle, pose par l'appelant (RGAA 11.1).
   *
   * Il vient de l'ENVELOPPE et non du nom de contrôle : dans un tableau, dix
   * lignes portent le meme `controlName`, et dix `id` identiques rendraient
   * l'attribut `for` des etiquettes ambigu — donc inutile. L'enveloppe, elle,
   * connait l'indice de ligne.
   *
   * Facultatif : un appelant qui ne le pose pas retrouve le comportement
   * anterieur, sans `id` ni liaison.
   */
  @Input() controlId?: string;

  /** L'identifiant du bloc d'erreurs, cible de `aria-describedby`. */
  get errorId(): string | null {
    return this.controlId ? `${this.controlId}-erreur` : null;
  }

  /**
   * Les erreurs que le gabarit sait rendre, hors `required` — conditionnee a
   * la soumission — et `isPatternInvalid`, qui ne vient pas du controle.
   *
   * Cette liste DOIT rester en phase avec les branches du gabarit : le bloc
   * entier est conditionne a `hasVisibleError`, donc une erreur affichee mais
   * absente d'ici ne serait jamais rendue, et le champ resterait sans
   * `aria-invalid` ni `aria-describedby`. `string-field.component.spec.ts`
   * tient un cas par cle pour empecher la derive.
   */
  private static readonly ERREURS_RENDUES = [
    'maxlength',
    'minlength',
    'email',
    'emailExists',
    'passwordStrength',
    'serverErrors',
    'nameTaken',
  ] as const;

  /** Le champ porte-t-il une erreur VISIBLE a cet instant ? */
  get hasVisibleError(): boolean {
    const erreurs = this.getControlErrors();

    if (!erreurs) {
      return this.isPatternInvalid;
    }

    return (
      (!!erreurs['required'] && this.submitted) ||
      StringFieldComponent.ERREURS_RENDUES.some((cle) => !!erreurs[cle]) ||
      this.isPatternInvalid
    );
  }

  valueChange = output<string>();

  getControlErrors() {
    return this.formGroup.controls[this.controlName]?.errors;
  }

  get isPatternInvalid() {
    const control = this.formGroup.controls[this.controlName];
    return (
      control?.hasError('pattern') && control.touched && control.value !== ''
    );
  }

  getRegexLimit() {
    const patternError =
      this.formGroup.controls[this.controlName].errors?.['pattern'];

    if (patternError) {
      const regex = patternError.requiredPattern;
      const matches = regex.match(/\{(\d+),(\d+)\}/); // Extraction des limites

      if (matches) {
        return {
          min: parseInt(matches[1], 10),
          max: parseInt(matches[2], 10),
        };
      }
    }
    return null; // Aucune erreur de pattern détectée
  }

  onKeyup(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    this.valueChange.emit(input.value);
  }
}
